import { UpdateEstateInsightsBundleConfigurationInput } from '@/types/schema';

import { diffUnordered } from '../diff/diffUnordered';
import { BundleSaveDescriptor } from '../presentationSave.types';
import { createFlatConfigDescriptor } from './flatConfigDescriptor';

/**
 * The estate insights bundle: a page toggle for each insight kind, plus the set
 * of household insights the bundle is linked to.
 *
 * The insights themselves are not this bundle's to write. The settings modal
 * assigns the household's whole insight list, and each insight is owned by the
 * insights module, so only the link is diffed here.
 */
export const estateInsightsBundleDescriptor: BundleSaveDescriptor =
  createFlatConfigDescriptor({
    field: 'estateInsightsBundleConfiguration',
    read: (bundle) => bundle.estateInsightsBundleConfiguration,
    diff: (previous, next) => {
      const update: UpdateEstateInsightsBundleConfigurationInput = {};

      if (
        previous.includeExSpouseListedInWillPage !==
        next.includeExSpouseListedInWillPage
      ) {
        update.includeExSpouseListedInWillPage =
          next.includeExSpouseListedInWillPage;
      }
      if (
        previous.includeMissingGuardianProvisionsPage !==
        next.includeMissingGuardianProvisionsPage
      ) {
        update.includeMissingGuardianProvisionsPage =
          next.includeMissingGuardianProvisionsPage;
      }
      if (
        previous.includeNoAlternativeAgentListedPoaPage !==
        next.includeNoAlternativeAgentListedPoaPage
      ) {
        update.includeNoAlternativeAgentListedPoaPage =
          next.includeNoAlternativeAgentListedPoaPage;
      }
      if (
        previous.includeNoAlternativeAgentListedHealthcareProxyPage !==
        next.includeNoAlternativeAgentListedHealthcareProxyPage
      ) {
        update.includeNoAlternativeAgentListedHealthcareProxyPage =
          next.includeNoAlternativeAgentListedHealthcareProxyPage;
      }
      if (
        previous.includeTrusteesNotUpdatedPage !==
        next.includeTrusteesNotUpdatedPage
      ) {
        update.includeTrusteesNotUpdatedPage =
          next.includeTrusteesNotUpdatedPage;
      }
      if (
        previous.includeExSpouseListedAsTrusteePage !==
        next.includeExSpouseListedAsTrusteePage
      ) {
        update.includeExSpouseListedAsTrusteePage =
          next.includeExSpouseListedAsTrusteePage;
      }
      if (
        previous.includeExSpouseListedAsBeneficiaryPage !==
        next.includeExSpouseListedAsBeneficiaryPage
      ) {
        update.includeExSpouseListedAsBeneficiaryPage =
          next.includeExSpouseListedAsBeneficiaryPage;
      }
      if (
        previous.includeAutomaticOutrightDistributionToSpousePage !==
        next.includeAutomaticOutrightDistributionToSpousePage
      ) {
        update.includeAutomaticOutrightDistributionToSpousePage =
          next.includeAutomaticOutrightDistributionToSpousePage;
      }
      if (
        previous.includeMissingPoaForSurivivingSpousePage !==
        next.includeMissingPoaForSurivivingSpousePage
      ) {
        update.includeMissingPoaForSurivivingSpousePage =
          next.includeMissingPoaForSurivivingSpousePage;
      }
      if (
        previous.includeOutrightDistributionsToChildrenPage !==
        next.includeOutrightDistributionsToChildrenPage
      ) {
        update.includeOutrightDistributionsToChildrenPage =
          next.includeOutrightDistributionsToChildrenPage;
      }
      if (
        previous.includeBypassTrustFundedSolelyViaDisclaimerPage !==
        next.includeBypassTrustFundedSolelyViaDisclaimerPage
      ) {
        update.includeBypassTrustFundedSolelyViaDisclaimerPage =
          next.includeBypassTrustFundedSolelyViaDisclaimerPage;
      }
      if (
        previous.includeCorporateTrusteePage !==
        next.includeCorporateTrusteePage
      ) {
        update.includeCorporateTrusteePage = next.includeCorporateTrusteePage;
      }
      if (
        previous.includeAnnualExclusionGiftingPage !==
        next.includeAnnualExclusionGiftingPage
      ) {
        update.includeAnnualExclusionGiftingPage =
          next.includeAnnualExclusionGiftingPage;
      }
      if (
        previous.includeFullUseOfLifetimeExemptionPage !==
        next.includeFullUseOfLifetimeExemptionPage
      ) {
        update.includeFullUseOfLifetimeExemptionPage =
          next.includeFullUseOfLifetimeExemptionPage;
      }
      if (
        previous.includeOpen529AccountPage !== next.includeOpen529AccountPage
      ) {
        update.includeOpen529AccountPage = next.includeOpen529AccountPage;
      }
      if (
        previous.includeMissingDocumentsPage !==
        next.includeMissingDocumentsPage
      ) {
        update.includeMissingDocumentsPage = next.includeMissingDocumentsPage;
      }
      if (
        previous.includeDifferentStateOfResidenceFromEstateDocumentsPage !==
        next.includeDifferentStateOfResidenceFromEstateDocumentsPage
      ) {
        update.includeDifferentStateOfResidenceFromEstateDocumentsPage =
          next.includeDifferentStateOfResidenceFromEstateDocumentsPage;
      }
      if (
        previous.includeSpecificBequestsPage !==
        next.includeSpecificBequestsPage
      ) {
        update.includeSpecificBequestsPage = next.includeSpecificBequestsPage;
      }
      if (
        previous.includeTrusteeDomiciledInDifferentState !==
        next.includeTrusteeDomiciledInDifferentState
      ) {
        update.includeTrusteeDomiciledInDifferentState =
          next.includeTrusteeDomiciledInDifferentState;
      }
      if (
        previous.includeRetitleAssetsToRevocableTrustPage !==
        next.includeRetitleAssetsToRevocableTrustPage
      ) {
        update.includeRetitleAssetsToRevocableTrustPage =
          next.includeRetitleAssetsToRevocableTrustPage;
      }
      if (previous.includeGiftToCharityPage !== next.includeGiftToCharityPage) {
        update.includeGiftToCharityPage = next.includeGiftToCharityPage;
      }
      if (
        previous.includeRetirementAccountBeneficiaryDesignationsPage !==
        next.includeRetirementAccountBeneficiaryDesignationsPage
      ) {
        update.includeRetirementAccountBeneficiaryDesignationsPage =
          next.includeRetirementAccountBeneficiaryDesignationsPage;
      }

      const insights = diffUnordered(
        previous.estateInsights.map((insight) => insight.id),
        next.estateInsights.map((insight) => insight.id)
      );
      if (insights) {
        if (insights.addIDs.length > 0) {
          update.addEstateInsightIDs = insights.addIDs;
        }
        if (insights.removeIDs.length > 0) {
          update.removeEstateInsightIDs = insights.removeIDs;
        }
      }

      // `displayName` is deliberately absent: this configuration has no
      // display-name field on the form, and the create path sends none either.
      return Object.keys(update).length > 0 ? update : null;
    },
    toPayload: (id, update) => ({
      updateEstateInsightsBundleConfiguration: { id, update },
    }),
  });
